import { useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SEGNAPOSTO, compila } from '@praticheenergia/domain'
import {
  SchemaModello, eProgrammato, eSollecito, elencaModelli, formattaData, salvaModello, type DatiModello, type Modello,
} from './email.api'

const NESSUNO: Modello[] = []

/** Valori di esempio per l'anteprima: chi scrive il modello deve vedere come
 * arriva al cliente, non le graffe. */
const ESEMPIO: Record<string, string> = {
  nome: 'Mario Rossi',
  azienda: 'Officina Rossi',
  impianto: 'ABC12345Z',
  anno: '2026',
  periodo: 'I semestre',
  scadenza: '31/03/2027',
  adempimento: 'Taratura contatori',
  link: 'https://portale.praticheenergia.com/…',
  partner: 'Studio Bianchi',
}

const VUOTO: DatiModello = { chiave: '', oggetto: '', corpo: '', mittente: '', attivo: true, dataInvio: '' }

export function GestioneEmail() {
  const queryClient = useQueryClient()
  const [modulo, setModulo] = useState<{ id: number | null; dati: DatiModello } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const { data: modelli = NESSUNO } = useQuery({ queryKey: ['modelli-email'], queryFn: elencaModelli })

  async function suSalva() {
    if (modulo === null) return
    const esito = SchemaModello.safeParse(modulo.dati)
    if (!esito.success) {
      setErrore(esito.error.issues[0]!.message)
      return
    }
    setErrore(null)
    try {
      await salvaModello(modulo.id, modulo.dati)
      setModulo(null)
      await queryClient.invalidateQueries({ queryKey: ['modelli-email'] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  const imposta = <K extends keyof DatiModello>(campo: K, valore: DatiModello[K]) =>
    setModulo((m) => (m === null ? m : { ...m, dati: { ...m.dati, [campo]: valore } }))

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Modelli email
      </Typography>
      {errore !== null && modulo === null && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{errore}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Chiave</TableCell>
            <TableCell>Oggetto</TableCell>
            <TableCell>Stato</TableCell>
            <TableCell>Invio</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {modelli.map((m) => (
            <TableRow key={m.id}>
              <TableCell>{m.chiave}</TableCell>
              <TableCell>{m.oggetto}</TableCell>
              <TableCell>
                <Chip size="small" label={m.attivo ? 'Attivo' : 'Spento'} color={m.attivo ? 'success' : 'default'} />
              </TableCell>
              <TableCell>{descriviInvio(m)}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  aria-label={`Modifica ${m.chiave}`}
                  onClick={() => {
                    setErrore(null)
                    setModulo({
                      id: m.id,
                      dati: {
                        chiave: m.chiave,
                        oggetto: m.oggetto,
                        corpo: m.corpo,
                        mittente: m.mittente ?? '',
                        attivo: m.attivo,
                        dataInvio: formattaData(m.invioMese, m.invioGiorno),
                      },
                    })
                  }}
                >
                  Modifica
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {modelli.length === 0 && <p>Nessun modello: il primo lo crei qui sotto</p>}

      <Button variant="outlined" sx={{ mt: 2 }} onClick={() => { setErrore(null); setModulo({ id: null, dati: VUOTO }) }}>
        Nuovo modello
      </Button>

      <Dialog open={modulo !== null} onClose={() => setModulo(null)} fullWidth maxWidth="md">
        <DialogTitle>{modulo?.id === null ? 'Nuovo modello' : `Modello ${modulo?.dati.chiave}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            <TextField
              size="small"
              label="Chiave"
              value={modulo?.dati.chiave ?? ''}
              disabled={modulo?.id !== null}
              helperText="Con questa chiave il portale chiede il modello: non cambia dopo la creazione"
              onChange={(e) => imposta('chiave', e.target.value)}
            />
            <TextField size="small" label="Oggetto" value={modulo?.dati.oggetto ?? ''} onChange={(e) => imposta('oggetto', e.target.value)} />
            <TextField
              size="small"
              label="Corpo"
              multiline
              minRows={6}
              value={modulo?.dati.corpo ?? ''}
              onChange={(e) => imposta('corpo', e.target.value)}
            />
            <TextField
              size="small"
              label="Mittente"
              value={modulo?.dati.mittente ?? ''}
              helperText="Vuoto: parte dall indirizzo predefinito di PraticheEnergia"
              onChange={(e) => imposta('mittente', e.target.value)}
            />
            {modulo !== null && eProgrammato(modulo.dati.chiave) && (
              <TextField
                size="small"
                label={eSollecito(modulo.dati.chiave) ? 'Non prima del (gg/mm)' : 'Data di invio (gg/mm)'}
                value={modulo.dati.dataInvio ?? ''}
                helperText={
                  eSollecito(modulo.dati.chiave)
                    ? 'Parte 20 giorni dopo la richiesta, se le letture mancano ancora. Vuoto: nessun vincolo di data'
                    : 'Parte da solo ogni anno in questa data. Vuoto: non parte'
                }
                onChange={(e) => imposta('dataInvio', e.target.value)}
              />
            )}
            <FormControlLabel
              control={<Switch checked={modulo?.dati.attivo ?? true} onChange={(e) => imposta('attivo', e.target.checked)} />}
              label="Attivo"
            />

            <Box>
              <Typography variant="subtitle2">Segnaposto disponibili</Typography>
              <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 1 }}>
                {Object.entries(SEGNAPOSTO).map(([chiave, spiegazione]) => (
                  <Chip key={chiave} size="small" label={`{{${chiave}}} — ${spiegazione}`} />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2">Anteprima con valori di esempio</Typography>
              <Box
                data-testid="anteprima"
                sx={{ border: 1, borderColor: 'divider', p: 1, mt: 1 }}
                // L'anteprima e' il modello del partner compilato: e' lui a
                // scriverne l'HTML, i valori passano da `compila` che li protegge.
                dangerouslySetInnerHTML={{ __html: compila(modulo?.dati.corpo ?? '', ESEMPIO) }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModulo(null)}>Annulla</Button>
          <Button variant="contained" onClick={() => void suSalva()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function descriviInvio(m: Modello): string {
  if (eSollecito(m.chiave)) {
    return m.invioMese === null
      ? '20 giorni dopo la richiesta'
      : `20 giorni dopo la richiesta, non prima del ${formattaData(m.invioMese, m.invioGiorno)}`
  }
  if (eProgrammato(m.chiave) && m.invioMese !== null) return `Ogni anno il ${formattaData(m.invioMese, m.invioGiorno)}`
  return 'Manuale'
}
